import type React from "react";
import { useEffect } from "react";

import { faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";

import type { UserCourseExercise } from "api/schemas/UserCourse";
import { IconButton } from "shared/atoms/IconButton";

import { ActiveTag } from "./ActiveTag";
import { ExerciseEditRow } from "./ExerciseEditRow";
import { JointTag } from "./SharedStyledComponents";

interface UserCourseExerciseProps {
  exercise: UserCourseExercise;
  currentProtocolWeek?: number;
  onChangeLevel: (exerciseId: number, level: number) => void;
  onDelete: (id: number) => void;
  onChangeWeek: (id: number, week: number) => void;
  onChangeEndWeek: (id: number, week: number | null) => void;
  onClickExercise: (id: number) => void;
  newlyAdded?: boolean;
  showActiveTag?: boolean;
}

export type ExerciseStatus = "active" | "planned" | "expired";

export const getExerciseStatus = (exercise: UserCourseExercise, currentProtocolWeek: number): ExerciseStatus => {
  const { active, introduction_protocol_week: startWeek, end_protocol_week: endWeek } = exercise;
  if (!active && startWeek > currentProtocolWeek) {
    return "planned";
  }
  if (!active && endWeek !== undefined && endWeek !== null && endWeek < currentProtocolWeek) {
    return "expired";
  }
  return "active";
};

export const UserCourseExerciseComponent: React.FC<UserCourseExerciseProps> = ({
  exercise,
  currentProtocolWeek,
  onChangeLevel,
  onChangeWeek,
  onChangeEndWeek,
  onDelete,
  onClickExercise,
  newlyAdded = false,
  showActiveTag = true,
}) => {
  const { id, introduction_protocol_week: startWeek, end_protocol_week: endWeek, target_joints: joints } = exercise;
  const status = currentProtocolWeek !== undefined && getExerciseStatus(exercise, currentProtocolWeek);

  const currentLevel = exercise.current_level;
  const { t } = useTranslation();
  let label;
  let variation;
  let image;

  // some user incorrectly has current_level set to 0, they need to be updated to level 1
  if (currentLevel > 0) {
    label = exercise.levels[currentLevel - 1].title;
    variation = exercise.levels[currentLevel - 1].variation_title;
    image = exercise.levels[currentLevel - 1].image_url || exercise.levels[currentLevel - 1].video_thumbnail_file_url;
  }
  useEffect(() => {
    if (currentLevel === 0) {
      onChangeLevel(id, 1);
    }
  }, [currentLevel]);

  return (
    <Root
      onClick={() => {
        onClickExercise(id);
      }}
      $newlyAdded={newlyAdded}
    >
      <Row>
        <Fill>
          <Title>
            {label}
            {variation ? ` (${variation})` : ""}
          </Title>
          <Tags>
            {showActiveTag && status && <ActiveTag status={status} startWeek={startWeek} endWeek={endWeek} />}
            {joints.map(v => (
              // FIXME: type translation
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <JointTag key={v}>{t(`patients.pain_location.${v}.0`)}</JointTag>
            ))}
          </Tags>
        </Fill>
        <FlexEnd>
          {image && (
            <ThumbWrapper>
              <img height="88" alt="" src={image} />
            </ThumbWrapper>
          )}
        </FlexEnd>
        <Relative>
          <DeleteButtonWrapper>
            <IconButton
              label=""
              onClick={e => {
                e.stopPropagation();
                onDelete(id);
              }}
            >
              <CloseIcon icon={faCircleXmark} />
            </IconButton>
          </DeleteButtonWrapper>
        </Relative>
      </Row>

      <ExerciseEditRow
        status={status || "active"}
        currentProtocolWeek={currentProtocolWeek || 0}
        exercise={exercise}
        onChangeEndWeek={onChangeEndWeek}
        onChangeLevel={onChangeLevel}
        onChangeWeek={onChangeWeek}
      />
    </Root>
  );
};

const ThumbWrapper = styled.div`
  max-width: 88px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Fill = styled.div`
  flex-grow: 1;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
  flex-grow: 1;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
`;

const newAnimKeyframes = keyframes`
  0% {
    box-shadow: 0px 0px 15px 0px #5096E8;
    transform: scale(1.1);
  }
  5% {
    transform: scale(1);
  }
  75% {
    box-shadow: 0px 0px 15px 0px #5096E8
  }
  100% {
    box-shadow: 0px 2px 12px 0px #0000000d;
  }
`;

const newAnim = css`
  animation: 5s ease-out ${newAnimKeyframes} forwards;
`;

const defaultValues = css`
  box-shadow: 0px 2px 12px 0px #0000000d;
  scale: 1;
`;

const Root = styled.div<{ $newlyAdded: boolean }>`
  padding: 16px;
  background-color: ${props => props.theme.colors.white};
  box-sizing: border-box;

  border-radius: 8px;
  ${props => (props.$newlyAdded ? newAnim : defaultValues)};
  display: flex;
  flex-direction: column;
  flex: 1 1 calc(33.33% - 16px);
  min-width: 300px;
  max-width: 395px;
`;

const CloseIcon = styled(FontAwesomeIcon)`
  width: 24px;
  height: 24px;
  color: #d4dbe2;
`;

const FlexEnd = styled.div`
  display: flex;
  justify-content: end;
`;

const Relative = styled.div`
  position: relative;
`;

const DeleteButtonWrapper = styled.div`
  position: absolute;
  right: -18px;
  top: -8px;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;
